'use client';

import { CSSProperties, useEffect } from 'react';

import { useTranslation } from '@lichtblick/i18next';
import { InfoOutlined, OpenInBrowser } from '@lichtblick/icons';
import { logger } from '@lichtblick/logger';
import { Colors, TextVariants } from '@lichtblick/theme';
import {
  AppointmentType,
  AppointmentTypeSelection,
  AppointmentTypeSelectionNullObject,
  Option,
} from '@lichtblick/types';

import { WorkOrderLink, WorkflowOptionContainer } from './AppointmentDrawer.styles';
import { AppointmentActions } from './AppointmentReducer';
import { useAppointmentDrawer } from './useAppointmentDrawer';

import {
  Autocomplete,
  AutocompleteMultiselect,
  Checkbox,
  CustomTimePicker,
  DatePicker,
  Foldout,
  Form,
  PopoverComponent,
  Text,
  Textarea,
} from '..';

export type AppointmentDrawerFormProps = ReturnType<typeof useAppointmentDrawer>;

export const AppointmentDrawerForm = ({
  appointment,
  appointmentDispatch,
  appointmentState,
  appointmentTypeName,
  appointmentTypeOptions,
  calculateMandatoryFields,
  checkIsNotTypes,
  checkIsType,
  craftmanListOptions,
  debouncedSetSearch,
  fetchDataFromWorkOrder,
  filteredBranchOptions,
  filteredPartnerOptions,
  filteredTeamOptions,
  filteredWorkOrderOptions,
  formErrors,
  isBlocker,
  mandatoryFields,
  onAppointmentTypeChange,
  onRemovePerson,
  onRemoveTeam,
  setAppointmentTypeName,
  setAppointmentTypeQuery,
  setBranchQuery,
  setCraftmanQuery,
  setFormErrors,
  setPartnerQuery,
  setTeamQuery,
  setWorkOrderQuery,
}: AppointmentDrawerFormProps) => {
  const { t } = useTranslation();
  const workOrderDisplayLabel =
    filteredWorkOrderOptions?.find(
      (wo: Option<string | number | undefined>) => wo.value === appointmentState.workOrder?.toString(),
    )?.label || appointmentState.workOrder;
  const compactEditRowStyle = appointment ? ({ '--form-field-min-width': '6rem' } as CSSProperties) : undefined;
  const currentSelection = appointmentState.appointmentType ?? AppointmentTypeSelectionNullObject;
  const resolveSelectionValue = (selection?: AppointmentTypeSelection | null) => {
    if (!selection || selection.id < 0) {
      return undefined;
    }

    return selection;
  };

  const shouldShowBranch = checkIsNotTypes(currentSelection, [AppointmentType.Krank]);
  const shouldShowPartner =
    checkIsNotTypes(currentSelection, [
      AppointmentType.Puffertag,
      AppointmentType.GeruestAufbau,
      AppointmentType.GeruestAbbau,
      AppointmentType.Warenlieferung,
      AppointmentType.Schulung,
      AppointmentType.KickoffCall,
      AppointmentType.Krank,
      AppointmentType.BueroTag,
      AppointmentType.Feiertag,
    ]) || appointmentTypeName === AppointmentType.OnlineBegehung;
  const shouldShowTeam = checkIsNotTypes(currentSelection, [
    AppointmentType.GeruestAbbau,
    AppointmentType.GeruestAufbau,
    AppointmentType.Warenlieferung,
    AppointmentType.KickoffCall,
    AppointmentType.Krank,
  ]);
  const shouldShowPeople =
    checkIsNotTypes(currentSelection, [
      AppointmentType.Feiertag,
      AppointmentType.GeruestAufbau,
      AppointmentType.GeruestAbbau,
      AppointmentType.Warenlieferung,
    ]) || appointmentTypeName === AppointmentType.OnlineBegehung;
  const shouldShowFromTime =
    checkIsNotTypes(currentSelection, [
      AppointmentType.Feiertag,
      AppointmentType.Puffertag,
      AppointmentType.GeruestAbbau,
      AppointmentType.GeruestAufbau,
      AppointmentType.Warenlieferung,
      AppointmentType.Schulung,
      AppointmentType.Urlaub,
      AppointmentType.Krank,
      AppointmentType.BueroTag,
    ]) || appointmentTypeName === AppointmentType.OnlineBegehung;
  const shouldShowToTime = checkIsType(currentSelection, [
    AppointmentType.VorOrtTerminWP,
    AppointmentType.VorOrtTermin,
    AppointmentType.ZaehlerSetzungstermin,
    AppointmentType.KickoffCall,
    AppointmentType.OnlineBegehung,
  ]);
  const shouldShowEndDate = checkIsNotTypes(currentSelection, [
    AppointmentType.VorOrtTerminWP,
    AppointmentType.VorOrtTermin,
    AppointmentType.ZaehlerSetzungstermin,
    AppointmentType.Warenlieferung,
    AppointmentType.KickoffCall,
    AppointmentType.OnlineBegehung,
  ]);

  useEffect(() => {
    logger.debug('Appointment drawer field visibility evaluated', {
      component: 'AppointmentDrawerForm',
      appointmentType: appointmentState.appointmentType,
      appointmentTypeName,
      isBlocker,
      shouldShowBranch,
      shouldShowPartner,
      shouldShowTeam,
      shouldShowPeople,
      shouldShowFromTime,
      shouldShowToTime,
      shouldShowEndDate,
      selectedOption: appointmentTypeOptions.find(
        (option: Option<AppointmentTypeSelection>) =>
          option.value === resolveSelectionValue(appointmentState.appointmentType),
      ),
    });
  }, [
    appointmentState.appointmentType,
    appointmentTypeName,
    appointmentTypeOptions,
    isBlocker,
    shouldShowBranch,
    shouldShowEndDate,
    shouldShowFromTime,
    shouldShowPartner,
    shouldShowPeople,
    shouldShowTeam,
    shouldShowToTime,
  ]);

  return (
    <Form.Root>
      {/* APPOINTMENT TYPE */}
      <Form.Row style={compactEditRowStyle}>
        <Form.Field>
          <Text $variant={TextVariants.body.m}>
            {t('pm:appointmentType')}
            <Text $variant={TextVariants.body.m} color={Colors.red[100]}>
              &nbsp;*
            </Text>
          </Text>
          {appointment ? (
            <Text $variant={TextVariants.body.m} color={Colors.gray[100]}>
              {appointmentTypeName}
            </Text>
          ) : (
            <Autocomplete<AppointmentTypeSelection>
              error={formErrors.appointmentType}
              getOptionKey={(option, index) => {
                const optionValue =
                  option && typeof option === 'object' && 'value' in option ? option.value : undefined;

                return optionValue ? `${optionValue.kind}:${optionValue.id}` : `n/a-${index}`;
              }}
              isDisabled={false}
              onSelect={(e) => {
                const selectedOption = appointmentTypeOptions.find(
                  (ap: Option<AppointmentTypeSelection>) => ap.value === e,
                );
                const selection =
                  selectedOption?.value && typeof selectedOption.value === 'object' && selectedOption.value !== null
                    ? (selectedOption.value as AppointmentTypeSelection)
                    : AppointmentTypeSelectionNullObject;

                setAppointmentTypeName(selectedOption?.label ?? '');
                setFormErrors({});
                calculateMandatoryFields(selection);
                onAppointmentTypeChange(selection);
              }}
              options={appointmentTypeOptions}
              setQuery={setAppointmentTypeQuery}
              value={resolveSelectionValue(currentSelection)}
            />
          )}
        </Form.Field>

        {/* WORK ORDER */}
        {!isBlocker && (
          <Form.Field>
            <Text $variant={TextVariants.body.m}>
              {t('pm:workOrder')}
              {mandatoryFields.includes('workOrder', 0) ? (
                <Text $variant={TextVariants.body.m} color={Colors.red[100]}>
                  &nbsp;*
                </Text>
              ) : null}
            </Text>
            {appointment ? (
              <>
                <Text $variant={TextVariants.body.m} color={Colors.gray[100]}>
                  {workOrderDisplayLabel}
                </Text>
                {appointmentState.workOrder && appointmentState.workOrder !== -1 && (
                  <WorkOrderLink
                    aria-label={t('pm:openWorkOrder', { id: appointmentState.workOrder })}
                    href={`/pm/installation/${appointmentState.workOrder}/appointments`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <OpenInBrowser />
                    <Text $variant={TextVariants.body.s} color="inherit">
                      {t('pm:openWorkOrder', { id: appointmentState.workOrder })}
                    </Text>
                  </WorkOrderLink>
                )}
              </>
            ) : (
              <Autocomplete
                error={formErrors.workOrder}
                isDisabled={false}
                onSelect={(e) => {
                  const selectedWorkOrderValue = typeof e === 'object' && e !== null ? undefined : e;

                  setFormErrors({});
                  fetchDataFromWorkOrder(selectedWorkOrderValue);
                  appointmentDispatch({
                    type: AppointmentActions.SET_WORK_ORDER,
                    payload: selectedWorkOrderValue ? parseInt(selectedWorkOrderValue.toString()) : -1,
                  });
                }}
                options={filteredWorkOrderOptions ?? []}
                setQuery={setWorkOrderQuery}
                value={appointmentState.workOrder?.toString()}
              />
            )}
          </Form.Field>
        )}
      </Form.Row>

      <Form.Row>
        {/* BRANCH */}
        {shouldShowBranch && (
          <Form.Field>
            <Text $variant={TextVariants.body.m}>
              {t('pm:branch')}
              {mandatoryFields.includes('branch') ? (
                <Text $variant={TextVariants.body.m} color={Colors.red[100]}>
                  &nbsp;*
                </Text>
              ) : null}
            </Text>
            <Autocomplete
              error={formErrors.branch}
              onSelect={(e) => {
                setFormErrors({});
                appointmentDispatch({
                  type: AppointmentActions.SET_BRANCH,
                  payload: e ? parseInt(e.toString()) : -1,
                });
              }}
              options={filteredBranchOptions ?? []}
              setQuery={setBranchQuery}
              value={appointmentState.branch?.toString()}
            />
          </Form.Field>
        )}
        {/* PARTNER COMPANY */}
        {shouldShowPartner && (
          <Form.Field>
            <Text $variant={TextVariants.body.m}>{t('pm:partner')}</Text>
            <Autocomplete
              onSelect={(e) => {
                setFormErrors({});
                appointmentDispatch({
                  type: AppointmentActions.SET_PARTNER,
                  payload: e ? parseInt(e.toString()) : -1,
                });
              }}
              options={filteredPartnerOptions}
              setQuery={setPartnerQuery}
              value={appointmentState.partner?.toString()}
            />
          </Form.Field>
        )}
      </Form.Row>

      <Form.Row>
        {/* TEAM */}
        {shouldShowTeam && (
          <Form.Field>
            <Text $variant={TextVariants.body.m}>{t('pm:team')}</Text>
            <AutocompleteMultiselect
              isDisabled={!!(isBlocker && appointmentState.teams?.length)}
              onRemoveItem={(team) => onRemoveTeam(team)}
              onSelect={(e) => {
                setFormErrors({});
                appointmentDispatch({
                  type: AppointmentActions.SET_TEAMS,
                  payload: e ? (e as Option[]) : [],
                });
              }}
              options={filteredTeamOptions ?? []}
              setQuery={setTeamQuery}
              values={appointmentState.teams ?? []}
            />
          </Form.Field>
        )}
        {/* PEOPLE */}
        {shouldShowPeople && (
          <Form.Field>
            <Text $variant={TextVariants.body.m}>
              {t('pm:person')}
              {isBlocker && (
                <Text $variant={TextVariants.body.m} color={Colors.red[100]}>
                  &nbsp;*
                </Text>
              )}
            </Text>
            <AutocompleteMultiselect
              isDisabled={!!(isBlocker && appointmentState.people.length)}
              onRemoveItem={(person) => onRemovePerson(person)}
              onRenderOption={(option) => {
                const optionSplit = option.label.split(' ');
                const email = optionSplit?.[optionSplit.length - 1] ?? '';

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Text $variant={TextVariants.body.m}>{`${optionSplit[0]} ${optionSplit[1]}`}</Text>
                    <Text $variant={TextVariants.body.s} color={Colors.gray[100]}>
                      {email}
                    </Text>
                  </div>
                );
              }}
              onSelect={(e) => {
                setFormErrors({});
                appointmentDispatch({ type: AppointmentActions.SET_PEOPLE, payload: e ? (e as Option[]) : [] });
              }}
              options={craftmanListOptions ?? []}
              setQuery={(e) => debouncedSetSearch(() => setCraftmanQuery(e))}
              values={appointmentState.people ?? []}
            />
          </Form.Field>
        )}
      </Form.Row>

      {/* START DATE */}
      <Form.Row>
        <Form.Field>
          <Text $variant={TextVariants.body.m}>
            {t('pm:startDate')}
            {mandatoryFields.includes('startDate') ? (
              <Text $variant={TextVariants.body.m} color={Colors.red[100]}>
                &nbsp;*
              </Text>
            ) : null}
          </Text>
          <DatePicker
            date={appointmentState.startDate}
            error={formErrors?.startDate}
            mode="single"
            setDate={(date) => {
              if (!(date instanceof Date)) {
                return;
              }

              if (appointmentState.startDate) {
                date.setHours(appointmentState.startDate.getHours());
                date.setMinutes(appointmentState.startDate.getMinutes());
              }

              appointmentDispatch({ type: AppointmentActions.SET_START_DATE, payload: date });
            }}
          />
        </Form.Field>

        {/* START DATE - FROM TIME */}
        {shouldShowFromTime && (
          <Form.Field>
            <Text $variant={TextVariants.body.m}>
              {t('pm:fromTime')}
              {mandatoryFields.includes('fromTime') ? (
                <Text $variant={TextVariants.body.m} color={Colors.red[100]}>
                  &nbsp;*
                </Text>
              ) : null}
            </Text>
            <CustomTimePicker
              error={formErrors.fromTime}
              fromTime={appointmentState.startDate ? appointmentState.startDate.toLocaleTimeString() : ''}
              setFromTime={(date) => {
                setFormErrors({});
                appointmentDispatch({ type: AppointmentActions.SET_FROM_TIME, payload: date });
              }}
            />
          </Form.Field>
        )}

        {/* START DATE - TO TIME */}
        {shouldShowToTime && (
          <Form.Field>
            <Text $variant={TextVariants.body.m}>
              {t('pm:toTime')}
              {mandatoryFields.includes('toTime') ? (
                <Text $variant={TextVariants.body.m} color={Colors.red[100]}>
                  &nbsp;*
                </Text>
              ) : null}
            </Text>
            <CustomTimePicker
              error={formErrors.toTime}
              setToTime={(date) => appointmentDispatch({ type: AppointmentActions.SET_TO_TIME, payload: date })}
              toTime={appointmentState.endDate ? appointmentState.endDate.toLocaleTimeString() : ''}
            />
          </Form.Field>
        )}
      </Form.Row>

      {/* END DATE */}
      {shouldShowEndDate && (
        <Form.Row>
          <Form.Field>
            <Text $variant={TextVariants.body.m}>
              {t('pm:endDate')}
              {mandatoryFields.includes('endDate') ?? (
                <Text $variant={TextVariants.body.m} color={Colors.red[100]}>
                  &nbsp;*
                </Text>
              )}
            </Text>
            <DatePicker
              date={appointmentState.endDate}
              error={formErrors.endDate}
              mode="single"
              setDate={(date) => {
                if (!(date instanceof Date)) {
                  return;
                }

                if (appointmentState.endDate) {
                  date.setHours(appointmentState.endDate.getHours());
                  date.setMinutes(appointmentState.endDate.getMinutes());
                }

                appointmentDispatch({ type: AppointmentActions.SET_END_DATE, payload: date });
              }}
            />
          </Form.Field>

          {/* END DATE - TO TIME */}
          {checkIsNotTypes(currentSelection, [
            AppointmentType.Feiertag,
            AppointmentType.WarenanlieferungWP,
            AppointmentType.VorarbeitMontageWP,
            AppointmentType.Puffertag,
            AppointmentType.GeruestAbbau,
            AppointmentType.GeruestAufbau,
            AppointmentType.Warenlieferung,
            AppointmentType.Schulung,
            AppointmentType.MontageWP,
            AppointmentType.Urlaub,
            AppointmentType.BueroTag,
            AppointmentType.Krank,
          ]) && (
            <Form.Field>
              <Text $variant={TextVariants.body.m}>{t('pm:time')}</Text>
              <CustomTimePicker
                error={formErrors.toTime}
                setToTime={(date) => appointmentDispatch({ type: AppointmentActions.SET_TO_TIME, payload: date })}
                toTime={appointmentState.endDate ? appointmentState.endDate.toLocaleTimeString() : ''}
              />
            </Form.Field>
          )}
        </Form.Row>
      )}

      {/* IS BINDING */}
      {(checkIsNotTypes(currentSelection, [
        AppointmentType.Feiertag,
        AppointmentType.Puffertag,
        AppointmentType.Urlaub,
        AppointmentType.Krank,
        AppointmentType.BueroTag,
      ]) ||
        checkIsType(currentSelection, [
          AppointmentType.OnlineBegehung,
          AppointmentType.B2BVorOrtTermin,
          AppointmentType.B2BBaustellenbegehung,
          AppointmentType.B2BACAbnahme,
          AppointmentType.B2BDCAbnahme,
        ])) && (
        <Checkbox
          isChecked={appointmentState.isBinding}
          label={t('pm:isBinding')}
          onChange={() => {
            setFormErrors({});
            appointmentDispatch({
              type: AppointmentActions.SET_IS_BINDING,
              payload: !appointmentState.isBinding,
            });
          }}
        />
      )}

      {/* NOTE */}
      {checkIsNotTypes(currentSelection, [AppointmentType.Krank]) && (
        <Form.Row>
          <Form.Field $span="full">
            <Text $variant={TextVariants.body.m} color={Colors.black}>
              {checkIsType(currentSelection, [
                AppointmentType.DCMontagetermin,
                AppointmentType.DCMontageterminVerlaengert,
                AppointmentType.DCNachbesserungsterminAGVerschuldet,
                AppointmentType.DCNachbesserungsterminInSeVerschuldet,
                AppointmentType.DCNachbesserungsterminKundeVerschuldet,
                AppointmentType.ACInstallation,
                AppointmentType.ACMontagetermin,
                AppointmentType.ACMontageterminVerlaengert,
                AppointmentType.ACMontageterminXXX,
                AppointmentType.ACNachbesserungsterminAGVerschuldet,
                AppointmentType.ACNachbesserungsterminInSeVerschuldet,
                AppointmentType.ACNachbesserungsterminKundeVerschuldet,
                AppointmentType.VorOrtTermin,
                AppointmentType.VorOrtTerminWP,
              ])
                ? t('pm:addYourNotesInfo')
                : t('pm:addYourNotes')}
            </Text>
            <Textarea
              maxLength={600}
              onChange={(e) =>
                appointmentDispatch({ type: AppointmentActions.SET_NOTE_TEXT, payload: e.currentTarget.value })
              }
              placeholder={t('pm:noteArea')}
              value={appointmentState.note}
            />
          </Form.Field>
        </Form.Row>
      )}

      {/* WORKFLOW OPTIONS (ADVANCED) */}
      {!appointment &&
        checkIsType(currentSelection, [
          AppointmentType.VorOrtTermin,
          AppointmentType.ACMontagetermin,
          AppointmentType.DCMontagetermin,
          AppointmentType.ACMontageterminVerlaengert,
          AppointmentType.DCMontageterminVerlaengert,
          AppointmentType.B2BVorOrtTermin,
          AppointmentType.B2BBaustellenbegehung,
          AppointmentType.B2BACAbnahme,
          AppointmentType.B2BDCAbnahme,
        ]) && (
          <Foldout isOpen={false} size="large" title={t('pm:workflowOptions')} titleVariant="m">
            <WorkflowOptionContainer $isReadonly={!!appointment}>
              <Checkbox
                isChecked={appointmentState.createNewWorkflowInstance}
                label={t('pm:createIndependentWorkflow')}
                onChange={
                  !appointment
                    ? () => {
                        appointmentDispatch({
                          type: AppointmentActions.SET_CREATE_NEW_WORKFLOW_INSTANCE,
                          payload: !appointmentState.createNewWorkflowInstance,
                        });
                      }
                    : undefined
                }
              />
              <PopoverComponent
                content={
                  <Text $variant={TextVariants.body.s} color={Colors.gray[100]}>
                    {t('pm:createIndependentWorkflowTooltip')}
                  </Text>
                }
                maxWidth="20rem"
                trigger={<InfoOutlined style={{ cursor: 'pointer', fontSize: '1.2rem', color: Colors.gray[100] }} />}
              />
            </WorkflowOptionContainer>
          </Foldout>
        )}
    </Form.Root>
  );
};
